/**
 * Hero Banner JavaScript
 * Adds interactive features and enhances the hero banner experience
 */

class HeroBanner {
  constructor() {
    // Find all hero banners on the page (could be multiple sections)
    this.banners = document.querySelectorAll('[id^="hero-banner-"]');
    if (this.banners.length === 0) return;
    
    // Initialize each banner
    this.banners.forEach(banner => {
      this.initBanner(banner);
    });
  }
  
  initBanner(banner) {
    this.banner = banner;
    this.init();
  }
  
  init() {
    // Set dynamic heights from section settings
    this.setDynamicHeight();
    
    // Set content alignment
    this.setContentAlignment();
    
    // Add scroll indicator if banner is tall enough
    this.addScrollIndicator();
    
    // Add parallax effect on scroll
    this.initParallaxEffect();
    
    // Add resize listener to handle responsive behavior
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  setDynamicHeight() {
    const height = this.banner.getAttribute('data-height') || 650;
    const mobileHeight = this.banner.getAttribute('data-mobile-height') || 500;
    
    this.banner.style.setProperty('--hero-height', `${height}px`);
    this.banner.style.setProperty('--hero-mobile-height', `${mobileHeight}px`);
  }
  
  setContentAlignment() {
    const alignment = this.banner.getAttribute('data-content-alignment') || 'center';
    const contentContainer = this.banner.querySelector('.hero-banner__content');
    
    if (contentContainer) {
      contentContainer.setAttribute('data-alignment', alignment);
    }
  }
  
  addScrollIndicator() {
    // The scroll indicator is now added in the liquid template
    // We just need to add the click event
    const scrollIndicator = this.banner.querySelector('.hero-banner__scroll-indicator');
    if (!scrollIndicator) return;
    
    // Add click event to scroll to next section
    scrollIndicator.addEventListener('click', () => {
      const nextSection = this.banner.nextElementSibling;
      if (nextSection) {
        window.scrollTo({
          top: nextSection.offsetTop,
          behavior: 'smooth'
        });
      }
    });
  }
  
  initParallaxEffect() {
    const image = this.banner.querySelector('.hero-banner__image');
    if (!image) return;
    
    // Store the banner for reference in the event listener
    const banner = this.banner;
    
    // Initial setup
    let lastScrollY = window.scrollY;
    let ticking = false;
    
    // Parallax function
    const parallax = () => {
      const scrollY = window.scrollY;
      const bannerRect = banner.getBoundingClientRect();
      
      // Only apply parallax if banner is in view
      if (bannerRect.bottom > 0 && bannerRect.top < window.innerHeight) {
        // Calculate parallax offset (slower movement for background)
        const offset = (scrollY - (banner.offsetTop - window.innerHeight)) * 0.4;
        image.style.transform = `translateY(${offset}px)`;
      }
      
      lastScrollY = scrollY;
      ticking = false;
    };
    
    // Scroll event with requestAnimationFrame for performance
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          parallax();
          ticking = false;
        });
        ticking = true;
      }
    });
    
    // Initial call to set position
    parallax();
  }
  
  handleResize() {
    // Reinitialize parallax effect on resize
    this.initParallaxEffect();
  }
}

// Initialize hero banner when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new HeroBanner();
});
